import { useLazyGetBuyersCertificateBySerialNumberQuery } from '@api/buyersCert.api';
import { useLazyGetDealerDetailsQuery } from '@api/customer.api';
import { useProductSearchQuery } from '@api/product.api';
import {
  BuyersCertificate,
  Dealer,
  DealerAddress,
  Warranty,
  WarrantyBrand,
  WarrantyClaimStatus,
  WarrantyPurchase,
} from '@api/types';
import { getPublicAddress } from '@components';
import { useAuth } from '@hooks';
import { getDealer } from '@store/dealer/dealer.slice';
import mapValues from 'lodash/mapValues';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

type WarrantyClaimContextProps = {
  children: React.ReactNode;
  warrantyData?: Warranty;
  partsDiscount?: number;
  laborPrice?: number;
  troubleshootingPrice?: number;
};

type WarrantyClaimContextType = {
  form: UseFormReturn<Warranty>;
  isBuyerWorking: boolean;
  dealerName?: string;
  dealerAddress?: DealerAddress | null;
  userEmail?: string | null;
  userFullName?: string | null;
  isSerialNoValid: boolean | null;
  setSerialNoValid: (value: boolean | null) => void;
  warrantyData?: Warranty;
  formValues: Warranty;
  resetProductFields: () => void;
  handleFileIdUpload: (attachmentId: number | null) => void;
  currentFileId: number | null;
  partsDiscount?: number;
  laborPrice?: number;
  troubleshootingPrice?: number;
  isReadOnly: boolean;
  toggleReadOnly: (readOnly: boolean) => void;
};

export const WarrantyClaimContext = createContext<WarrantyClaimContextType | null>(null);

export const WarrantyClaimProvider = ({
  children,
  warrantyData,
  partsDiscount,
  laborPrice,
  troubleshootingPrice,
}: WarrantyClaimContextProps) => {
  const [isReadOnly, setReadOnly] = useState(false);
  useProductSearchQuery({});
  const toggleReadOnly = useCallback((readOnly: boolean) => setReadOnly(readOnly), []);

  const dealer: Dealer = useSelector(getDealer);
  const dealerName = getPublicAddress(dealer)?.name;
  const { email: userEmail, customerNo, name: userFullName, isBeremaUser } = useAuth();
  const dealerAddress = useMemo(
    () => dealer?.address?.find((a) => a.addressType === 'Public'),
    [dealer]
  );
  const [isSerialNoValid, setIsSerialNoValid] = useState<boolean | null>(null);
  const setSerialNoValid = useCallback((value: boolean | null) => {
    setIsSerialNoValid(value);
  }, []);
  const [currentFileId, setCurrentFileId] = useState<number | null>(
    warrantyData?.recipeFile?.[0]?.id ?? null
  );

  const handleFileIdUpload = useCallback((attachmentId: number | null) => {
    setCurrentFileId(attachmentId);
  }, []);

  const [trigger, response] = useLazyGetBuyersCertificateBySerialNumberQuery();
  const { isLoading: isBuyerLoading, isFetching: isBuyerFetching } = response;
  const form = useForm<Warranty>({
    defaultValues: warrantyData
      ? mapValues(warrantyData, (value) => value ?? null)
      : {
          brand: WarrantyBrand.Honda,
          purchase: WarrantyPurchase.Private,
          ...(isBeremaUser ? { claimType: null } : {}),
          dealerEmail: userEmail,
          status: WarrantyClaimStatus.New,
          serialNo: null,
          partsDiscount: partsDiscount || 0,
          laborPrice: laborPrice || 0,
          troubleshootingPrice: troubleshootingPrice || 0,
        },
  });
  const formValues = form.getValues();

  const [fetchDealer] = useLazyGetDealerDetailsQuery();
  const isBuyerWorking = isBuyerLoading || isBuyerFetching;

  const resetProductFields = useCallback(() => {
    form.setValue('productModel', undefined);
    form.setValue('productType', undefined);
    form.setValue('engineNo', undefined);
    form.setValue('saleDate', undefined);
    form.setValue('purchase', WarrantyPurchase.Private);
  }, [form]);

  useEffect(() => {
    if (customerNo && !warrantyData) {
      fetchDealer(customerNo);
    }
  }, [customerNo, fetchDealer, form, warrantyData]);

  useEffect(() => {
    if (dealerAddress && !warrantyData) {
      form.setValue('dealerName', dealerAddress?.name);
      form.setValue('dealerContact', userFullName || undefined);
      form.setValue('dealerEmail', userEmail || undefined);
    }
  }, [form, warrantyData, dealerAddress, userFullName, userEmail]);

  useEffect(() => {
    const savedFormData = localStorage.getItem('warrantyFormData');

    if (isSerialNoValid === true && !savedFormData) {
      trigger(form.watch('serialNo') || '').then((res) => {
        const data: BuyersCertificate | undefined = res.data?.data;

        if (data) {
          form.setValue('productModel', data.productModel);
          form.setValue('productType', data.productType);
          form.setValue('engineNo', data.engineNumber);
          form.setValue('saleDate', data.salesDate);
          form.setValue('purchase', data.purchaseType || WarrantyPurchase.Private);
          form.setValue('customerName', data.customerName);
          form.setValue('customerAddress', data.customerAddress);
          form.setValue('customerPostCode', data.customerPostCode);
          form.setValue('customerCity', data.customerCity);
        } else {
          resetProductFields();
        }
      });
    }
  }, [isSerialNoValid, trigger, form, resetProductFields]);

  const contextValue = useMemo(
    () => ({
      form,
      isBuyerWorking,
      dealerName,
      userEmail,
      userFullName,
      isSerialNoValid,
      setSerialNoValid,
      warrantyData,
      formValues,
      resetProductFields,
      handleFileIdUpload,
      currentFileId,
      isReadOnly,
      toggleReadOnly,
    }),
    [
      form,
      userFullName,
      isBuyerWorking,
      dealerName,
      userEmail,
      isSerialNoValid,
      setSerialNoValid,
      warrantyData,
      formValues,
      resetProductFields,
      handleFileIdUpload,
      currentFileId,
      isReadOnly,
      toggleReadOnly,
    ]
  );
  return (
    <WarrantyClaimContext.Provider value={contextValue}>{children}</WarrantyClaimContext.Provider>
  );
};

export const useWarrantyClaimContext = () => {
  const context = useContext(WarrantyClaimContext);
  if (!context) {
    throw new Error('useWarrantyClaimContext must be used within a WarrantyClaimContextProvider');
  }
  return context;
};
